import { apiClient } from "@/lib/api/api-client";
import { BaseResponse } from "@/modules/shared/types/baseResponse";
import {
  JobReport,
  Pipeline,
  Rating,
  ScoreBucket,
  ScoreBucketRange,
} from "@/modules/types/dashboard";
import moment from "moment";

export type DashboardReportQuery = {
  businessId: string;
  startDate?: string;
  endDate?: string;
  jobId?: string;
};

export interface DashboardReportsResponse extends BaseResponse {
  jobReports: JobReport[];
  total: string;
  /** All interviews scheduled for today (business-wide; not per job row). */
  interviewsToday: number;
}

/** Go int64 often arrives as a JSON string (protojson, json:",string", or large IDs). */
function toInt64(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }
  return 0;
}

function toFloat(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function normalizePipeline(raw: unknown): Pipeline {
  const p =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    new: toInt64(p.new),
    reviewing: toInt64(p.reviewing),
    processing: toInt64(p.processing),
    discarded: toInt64(p.discarded),
    hired: toInt64(p.hired),
  };
}

function normalizeRating(raw: unknown): Rating {
  const r =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    salary: toFloat(r.salary),
    environment: toFloat(r.environment),
    process: toFloat(r.process),
    growth: toFloat(r.growth),
    support: toFloat(r.support),
  };
}

function normalizeScoreBuckets(raw: unknown): ScoreBucket[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((b) => {
    const row =
      b && typeof b === "object" ? (b as Record<string, unknown>) : {};
    return {
      range: String(row.range ?? "") as ScoreBucketRange,
      count: toInt64(row.count),
    };
  });
}

function normalizeJobReport(raw: unknown): JobReport {
  const r =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    id: String(r.id ?? ""),
    date: String(r.date ?? ""),
    jobId: String(r.jobId ?? ""),
    jobTitle: String(r.jobTitle ?? ""),
    jobGroup: String(r.jobGroup ?? ""),
    jobType: r.jobType as JobReport["jobType"],
    views: toInt64(r.views),
    applications: toInt64(r.applications),
    newCandidates: toInt64(r.newCandidates),
    pipeline: normalizePipeline(r.pipeline),
    aiScoreBuckets: normalizeScoreBuckets(r.aiScoreBuckets),
    rating: normalizeRating(r.rating),
  };
}

export class DashboardService {
  private BASE_PATH =
    process.env.API_JOB_ENDPOINT || "http://localhost:8001/job";
  private apiClient = apiClient;
  private apiVersion = "v1.0.0";

  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async getReports(
    query: DashboardReportQuery,
  ): Promise<DashboardReportsResponse> {
    const queryParam = new URLSearchParams();
    queryParam.append("businessId", query.businessId);
    if (query.startDate)
      queryParam.append("startDate", String(moment(query.startDate).unix()));
    if (query.endDate)
      queryParam.append("endDate", String(moment(query.endDate).unix()));
    if (query.jobId) queryParam.append("jobId", query.jobId);

    const res = await this.apiClient.fetch<
      Omit<DashboardReportsResponse, "interviewsToday"> & {
        interviewsToday?: string | number;
      }
    >(`${this.BASE_PATH}/${this.apiVersion}/report?${queryParam.toString()}`);
    return {
      ...res,
      interviewsToday: toInt64(res.interviewsToday),
      jobReports: (res.jobReports ?? []).map(normalizeJobReport),
    };
  }
}
